// Widget 是浮窗 UI。它订阅 Client 的事件并渲染 DOM；不直接管理 WS。
// 设计原则：
//   - 不依赖任何前端框架，所有 DOM 操作走原生 API
//   - CSS 用 .leke- 前缀避免污染宿主页

import { Client, ClientEvent } from './client';
import { MsgSendPayload, TYPING_PREVIEW_MAX_RUNES } from './protocol';
import { getMessages, Locale, Messages, resolveLocale } from './i18n';
import { playBeep, shouldNotify, showNotification } from './notify';
import { EMOJI_GROUPS } from './emojis';

import widgetCss from './widget.css?inline';

// 输入区图标（内联 SVG，随 currentColor 着色）
const ICON_PLUS =
  '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
const ICON_SEND =
  '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M6 11l6-6 6 6"/></svg>';

// 输入预览的发送节奏（仅租户开启 typingPreviewEnabled 时生效）。
// 停顿 DEBOUNCE 后发一帧全量文本；但连续打字时 debounce 永不触发，
// 所以再加 MAX_WAIT 作为强制下限，否则坐席会一直停在第一帧的内容上。
const TYPING_PREVIEW_DEBOUNCE_MS = 300;
const TYPING_PREVIEW_MAX_WAIT_MS = 1000;

/** 客户端可识别的附件子类型。 */
export type AttachmentKind = 'image' | 'video' | 'file';

/** 单文件大小上限（与后端 biz-api 配置对齐）。 */
// 嵌入访客站的聊天 widget 是长生命周期 DOM：消息节点无上限累积会持续吃内存、
// 拖慢重排。超过此数即从顶部回收最旧节点（聊天始终锚定底部，影响不可见）。
const MAX_RENDERED_MESSAGES = 200;

export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
const MAX_UPLOAD_MB = Math.floor(MAX_UPLOAD_BYTES / (1024 * 1024));

export interface AttachmentMeta {
  url: string;
  name: string;
  size: number;
  mime: string;
  /** presign 返回的 attachmentId，透传到服务端用于把 pending 关联到 msg.id */
  attachmentId?: string;
  /** 图文合并：随附件一并发送的图注文字，渲染时在媒体下方同气泡显示 */
  text?: string;
  /** 图文合并：一条 image 消息可携带多张图片，渲染时同气泡内平铺；无则回退到单张 url */
  images?: { url: string; name?: string }[];
}

export interface WidgetOptions {
  client: Client;
  /** 取当前会话 ID（每次 send 时调，便于 init 异步设置） */
  getConvId: () => string;
  /** 取对端 userKey（生产模式可返回空字符串：服务端按会话派生） */
  getTo: () => string;
  /** 单条文字字数上限 */
  textLimit?: number;
  /** 标题文案；不传时使用 i18n 默认标题 */
  title?: string;
  /** 显示语言；不传时按 navigator.language 自动识别 */
  locale?: Locale;
  /** 上传文件回调：实现 presign + PUT，返回元信息后 Widget 发送对应类型消息。
   *  未配置时附件按钮被禁用。 */
  uploadFile?: (file: File, kind: AttachmentKind) => Promise<AttachmentMeta>;
  /** 访客点击"结束会话"时的回调：调用方负责调用 biz-api 并完成清理。
   *  返回 promise 让 Widget 等待完成后再切到 ended 状态。 */
  endConversation?: () => Promise<void>;
  /** 访客在已结束会话里点"重新开始对话"的回调：调用方负责重置会话状态
   *  （清空 convId 等），使下一次发送创建一条新会话。不传则不显示该按钮。
   *  旧对话记录保留在界面上，不清空。 */
  restartConversation?: () => Promise<void> | void;
  /** 访客提交评价的回调；调用方负责 POST 到 biz-api。
   *  未配置时不显示评价面板。 */
  submitRating?: (rating: number, comment: string) => Promise<void>;
  /** 历史评分；非空表示已评过，不再弹评价面板。 */
  initialRating?: number | null;
  /** 客户级是否允许评价。false 时即便 submitRating 已配置也不显示评价面板。 */
  ratingFeatureEnabled?: boolean;
  /** 客户级是否展示已读回执（当前开关，可被服务端实时变更）。false 时自己的消息
   *  停留「已送达」,不显示「已读」。缺省（undefined）视为开启。 */
  readReceiptEnabled?: boolean;
  /** 客户级是否把访客正在输入的内容实时发给坐席。缺省（undefined）视为关闭——
   *  未发送的内容对坐席可见属隐私敏感面,须租户显式开启。关闭时只发 typing 状态。 */
  typingPreviewEnabled?: boolean;
  /** 新消息提示音；默认开启 */
  soundEnabled?: boolean;
  /** 桌面通知；默认开启（实际仅在用户授权后才弹） */
  notificationEnabled?: boolean;
  /** 延迟连接钩子：访客第一次点击发送时调用，调用方负责"创建/复用会话 + 建立 WS"。
   *  resolve 之后 widget 才会调 client.send。返回 reject 时 widget 显示系统错误提示。
   *  不传时按旧逻辑（外层提前调用 client.connect）。 */
  ensureReady?: () => Promise<void>;
  /** 坐席结束会话（服务端下行 conv_ended）时回调，调用方据此清理 stored convId 等本地状态，
   *  避免访客刷新后被自动恢复为旧会话再被 GetOrCreate 成新会话进队列。 */
  onAgentEnded?: () => void;
  /** 隐藏 SDK 自带悬浮球：宿主网站用自己的按钮/图标触发展开（AiKeChat.open()）时开启。
   *  面板与遮罩不受影响，仍可正常展开。 */
  hideLauncher?: boolean;
}

/** 历史消息条目，结构对齐 biz-api `GET /v1/visitor/conversations/:id/messages` 响应。 */
export interface HistoryItem {
  id: string;
  senderType: 'visitor' | 'agent' | 'bot' | 'system';
  senderName?: string;
  contentType: string;
  content: unknown;
  quoteOfMsgId?: string;
  quote?: HistoryQuote;
  recalled?: boolean;
  createdAt: string;
  /** 仅跨会话历史返回，前端据此画会话分隔 */
  convId?: string;
}

/** 跨会话历史分页加载器：before 为空取最新一页，否则取早于该时刻的一页。 */
export type HistoryPager = (
  before?: string,
) => Promise<{ items: HistoryItem[]; hasMore: boolean }>;

/** 历史消息里嵌套的"被引用消息"摘要（结构与服务端 message.handler.go 的 quoteDTO 对齐） */
export interface HistoryQuote {
  msgId: string;
  senderType: 'visitor' | 'agent' | 'bot' | 'system';
  senderName?: string;
  contentType: string;
  content?: unknown;
  recalled?: boolean;
  createdAt: string;
}

interface MessageView {
  id: string;
  side: 'self' | 'peer' | 'system';
  /** 对端显示名（peer 消息上方头部展示）：agent/bot/system 不同来源不同 */
  senderName?: string;
  /** 文本消息 */
  text?: string;
  /** 附件消息 */
  attachment?: { kind: AttachmentKind; meta: AttachmentMeta };
  ts: number;
  state?: 'sending' | 'sent' | 'read' | 'failed';
  /** 已撤回；渲染时显示"[消息已撤回]"占位 */
  recalled?: boolean;
  /** 引用了另一条历史消息时的预览块 */
  quote?: MessageQuoteView;
  /** 仅本地展示、服务端无对应记录（如欢迎语）：不可被引用 */
  local?: boolean;
}

interface MessageQuoteView {
  msgId: string;
  senderType: 'visitor' | 'agent' | 'bot' | 'system';
  senderName?: string;
  contentType: string;
  /** 文本类的纯文本摘要（图片/文件等已转成 [图片] 这种占位） */
  summary: string;
  recalled?: boolean;
}

export class Widget {
  private opts: Required<Pick<WidgetOptions, 'textLimit'>> & WidgetOptions;
  private t: Messages;
  private el: {
    launcher: HTMLButtonElement;
    panel: HTMLDivElement;
    backdrop: HTMLDivElement;
    header: HTMLDivElement;
    headerStatus: HTMLDivElement;
    messages: HTMLDivElement;
    textarea: HTMLTextAreaElement;
    sendBtn: HTMLButtonElement;
    hint: HTMLDivElement;
    attachBtn: HTMLButtonElement;
    fileInput: HTMLInputElement;
    endBtn: HTMLButtonElement;
    ratingPanel: HTMLDivElement;
    emojiBtn: HTMLButtonElement;
    emojiPanel: HTMLDivElement;
    queueBar: HTMLDivElement;
  };
  private rated = false;
  private currentStars = 0;
  private unread = 0;
  private open = false;
  private ended = false;
  // 本次结束是访客自己点的「结束对话」。后端 End 会给双方各发一帧 conv_ended、
  // 内容相同不区分发起方，访客侧收到后不能再报「坐席已结束对话」。
  private endedByMe = false;
  // 宿主页滚动锁：面板打开时定住 body，避免 iOS 可视视口可拖 + 触摸透传宿主页
  private bodyLocked = false;
  private scrollLockY = 0;
  // 键盘态下拦截 touchmove，禁掉 iOS 可视视口平移(pan)
  private touchMoveBlocker: ((e: TouchEvent) => void) | null = null;
  private restartRow: HTMLDivElement | null = null;
  private msgs = new Map<string, MessageView>();
  private unsubscribe: () => void;
  // typing 状态
  private lastTypingSentAt = 0;
  private lastTypingState: 'start' | 'stop' | 'none' = 'none';
  private localTypingStopTimer: number | null = null;
  // 输入预览（typingPreviewEnabled 开启时才用到）
  private previewDebounceTimer: number | null = null;
  private lastPreviewSentAt = 0;
  // 读取标记：自己已经上报过的最后已读 id，避免重复上报；对端最新消息 id
  private lastReadSent = '';
  private latestPeerMsgId = '';
  // 当前会话坐席名（conv ready / 转接时填）；收到 agent 消息时统一显示此名（含协同坐席）
  private agentName = '';
  // 已渲染的最后一条消息所属"本地自然日"，用于按天插入日期分隔条
  private lastDayKey = '';
  // 当前选中的引用消息（点击气泡引用按钮后填；发送后清空）
  private quoting: MessageQuoteView | null = null;
  // 引用条 DOM（lazy 创建）
  private quoteBarEl: HTMLDivElement | null = null;
  // 服务端配置的浮窗标题（auth 响应里的 settings.widgetTitle）
  // 优先级链：opts.title > this.serverTitle > i18n.title
  private serverTitle = '';
  // 租户品牌 logo（auth 响应里的 settings.logoUrl）；空 = 顶部与坐席气泡都不展示
  private logoUrl = '';
  // 记录最后一次 client 状态，便于 setLocale 时按相同状态重渲文案
  private lastStatus:
    | { kind: 'connecting' }
    | { kind: 'open' }
    | { kind: 'close' }
    | { kind: 'reconnecting'; attempt: number; delay: number } = { kind: 'connecting' };
  // emoji 面板点外面关闭用的 document 监听器
  private outsideClickHandler: ((ev: Event) => void) | null = null;
  // 标签页重新可见时补发 msg.read 用的 document 监听器
  private visibilityHandler: (() => void) | null = null;
  // H5 软键盘避让：visualViewport 监听器（无该 API 的浏览器为 null）
  private viewportHandler: (() => void) | null = null;

  constructor(opts: WidgetOptions) {
    // 用 spread 兜底会被 undefined 字段覆盖，因此显式 ?? 处理。
    this.opts = {
      ...opts,
      textLimit: opts.textLimit ?? 480,
    };
    const locale = resolveLocale(opts.locale);
    this.t = getMessages(locale);
    if (opts.initialRating && opts.initialRating > 0) this.rated = true;
    this.injectStyle();
    this.el = this.buildDom();
    this.bindEvents();
    this.unsubscribe = this.opts.client.on((e) => this.onClientEvent(e));
    // 访客在别的标签页时,收到对端消息走 maybeSendRead 会被 visibilityState=hidden
    // 挡掉(见 maybeSendRead),read 不上报。切回本标签页时没有任何事件重新触发,
    // 坐席端"已送达"永远变不成"已读"。这里补一个 visibilitychange 监听:回到
    // 可见且浮窗打开时补发。maybeSendRead 自带 open/可见/去重判断,重复调安全。
    if (typeof document !== 'undefined') {
      this.visibilityHandler = () => {
        if (document.visibilityState === 'visible' && this.open) this.maybeSendRead();
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
    this.bindKeyboardInset();
  }

  // bindKeyboardInset：H5 软键盘弹出时的面板避让，随 visualViewport 实时调整。
  //
  // iOS Safari 弹键盘只缩 visual viewport、不动 layout viewport，position:fixed 面板
  // 仍贴屏幕底、输入框被键盘盖住（dvh 也救不了），且键盘态下可视视口还能被上下 pan。
  // 两条路径(见 viewportHandler 内注释)：
  //  - 浮窗底栏(移动端)：面板高度撑满布局视口(clientH) + top 跟随可视视口顶 +
  //    padding-bottom=键盘高，既把输入框顶到键盘上，又向下多盖住键盘背后不露黑。
  //  - chat.html 全屏 / 桌面：其 height/inset 被 !important 锁死，改高度无效，退回用
  //    padding-bottom 单独消化键盘高度。
  // 另有一类宿主页(如 freewillex 这类 SPA)在输入框 focus 时会把窗口整体滚动 ≈键盘高，
  // fixed 面板随布局视口被顶走、输入框与键盘间露大块空白；见下方 scrollTo 抵消。
  // 面板未打开时写入也无副作用（kbUp 恒 false），故常驻监听。
  private bindKeyboardInset() {
    const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
    if (!vv) return; // 老浏览器：保持原行为，不做避让
    this.viewportHandler = () => {
      // layout viewport 高度：iOS 弹键盘不缩它(真机实测 clientH 恒定)，作稳定参照。
      const layoutH = document.documentElement.clientHeight;
      // 键盘高度 = 布局视口高 − 可视视口高。关键：不能再减 vv.offsetTop——用户上滑
      // 时 iOS 会 pan 可视视口令 offsetTop 变大，一旦减掉，键盘还在也会把 kbHeight
      // 算到趋 0、kbUp 误判为 false，进而把面板错误还原成 80dvh，空白/滚动条重现
      // (即之前的「两种状态」)。offsetTop 只用于下面 top 跟随，不参与判定。
      const kbHeight = layoutH - vv.height;
      const kbUp = kbHeight > 80; // 地址栏抖动只有几十 px，阈值滤掉

      // 宿主页(如 freewillex 这类 SPA)在输入框 focus 时会把窗口滚动 ≈键盘高，fixed 面板
      // 随布局视口一起被顶走 → 输入框与键盘间露大块空白。lockHostScroll 的 body:fixed 在
      // 这类页面锁不住(可滚的不是 body)。这里主动把窗口滚回锁定位置抵消它，让 fixed 面板
      // 定位恢复正常(真机 HUD 实测:坏态 winScrollY=347、docEl.top=-347 即此)。
      if (
        kbUp &&
        this.open &&
        window.innerWidth < 768 &&
        vv.scale <= 1.01 && // 未缩放才抵消:缩放页(允许 user-scalable)聚焦会自动放大+平移，
        Math.abs(window.scrollY - this.scrollLockY) > 1 // 那是正常缩放平移，别去和它打架
      ) {
        window.scrollTo(0, this.scrollLockY);
      }

      // 判浮窗底栏 vs chat.html 全屏：全屏对 border-radius 加了 !important 置 0，
      // 底栏是 16px。用它当判据——不受键盘影响、也不被下面的改动污染(我们不碰圆角)。
      const bottomSheet =
        window.innerWidth < 768 &&
        getComputedStyle(this.el.panel).borderTopLeftRadius !== '0px';

      const p = this.el.panel.style;
      if (bottomSheet) {
        // 浮窗底栏(移动端)键盘避让。真机实测:iOS 键盘态下布局视口(clientH)不缩、
        // 可视视口(vv)缩且可被 pan(自动聚焦滚动 + 手动拖),JS 无法可靠禁掉。
        // 若面板只盖可视视口(height=vv.h),底边切在键盘顶,键盘半透明候选条背后就
        // 透出宿主页黑底;pan 拖尾时更会露黑/露白。
        //
        // 解法:面板高度 = 整个布局视口高(clientH),向下多盖住键盘背后那段——不露黑;
        // top 跟随 vv.offsetTop 让面板顶对齐可视窗口顶(header 可见);padding-bottom =
        // 键盘高度把输入框稳定顶到可视窗口底=键盘顶。收起还原 CSS 的 80dvh 底栏。
        if (kbUp) {
          // 关掉 CSS 的 padding-bottom 0.18s 过渡：避让靠 height/top 瞬时完成。
          p.transition = 'none';
          p.setProperty('--leke-kb', Math.round(kbHeight) + 'px');
          p.height = Math.round(layoutH) + 'px';
          // 用 top 定位不用 transform：iOS 键盘态下对 fixed 面板忽略 translateY(真机实测
          // translateY 后面板仍停在 top=0),但认 top。
          p.top = Math.round(vv.offsetTop) + 'px';
          p.bottom = 'auto';
          p.transform = '';
        } else {
          p.transition = '';
          p.height = '';
          p.top = '';
          p.bottom = '';
          p.transform = '';
          p.setProperty('--leke-kb', '0px');
        }
      } else {
        // chat.html 全屏 / 桌面：沿用 padding-bottom 避让(全屏对 height/inset 加了
        // !important，改高度无效；桌面无软键盘 kb 恒 0)。
        const maxKb = Math.max(0, this.el.panel.offsetHeight - 160);
        const kb = kbUp ? Math.min(Math.round(kbHeight), maxKb) : 0;
        p.setProperty('--leke-kb', kb + 'px');
      }

      // 键盘态下把会话吸附到底(最后一条贴输入框)。不能只在 kbUp 翻转的第一帧滚——
      // 那一帧面板还没缩到 clientH/键盘还没定型、消息区没溢出，滚到底=没动；等动画把
      // 消息区压小、内容溢出后已不再滚，就停在顶部。故整个开合动画期间(每个 vv 事件)
      // 都吸底；键盘稳定后 vv 不再触发，也就不会打扰用户手动上滑看历史。rAF 确保在本次
      // 高度/padding 改动后的布局上测量 scrollHeight。
      if (kbUp && this.open) {
        requestAnimationFrame(() => {
          this.el.messages.scrollTop = this.el.messages.scrollHeight;
        });
      }
    };
    vv.addEventListener('resize', this.viewportHandler);
    vv.addEventListener('scroll', this.viewportHandler);
  }

  // setLocale 切换 UI 语言并刷新所有静态文本节点。
  // 非破坏性：保留已存在的对话消息、评分状态、连接状态文案。
  setLocale(loc?: string | null) {
    const next = resolveLocale(loc ?? undefined);
    this.t = getMessages(next);
    // launcher / panel header
    this.el.launcher.setAttribute('aria-label', this.t.launcherAriaLabel);
    const titleEl = this.el.header.querySelector<HTMLElement>('.leke-header-title');
    if (titleEl) titleEl.textContent = this.resolveTitle();
    // status：按记录的状态重渲
    switch (this.lastStatus.kind) {
      case 'open':
        this.el.headerStatus.textContent = this.t.statusOnline;
        break;
      case 'close':
        this.el.headerStatus.textContent = this.t.statusDisconnected;
        break;
      case 'reconnecting':
        this.el.headerStatus.textContent = this.t.statusReconnecting(
          this.lastStatus.attempt,
          this.lastStatus.delay,
        );
        break;
      default:
        this.el.headerStatus.textContent = this.t.statusConnecting;
    }
    // 顶部按钮
    this.el.endBtn.title = this.t.endConversation;
    this.el.endBtn.textContent = this.t.endConversation;
    const closeBtn = this.el.header.querySelector<HTMLElement>('.leke-header-close');
    if (closeBtn) closeBtn.setAttribute('aria-label', this.t.closeAriaLabel);
    // 输入区
    this.el.textarea.placeholder = this.t.textareaPlaceholder;
    this.el.emojiBtn.setAttribute('aria-label', this.t.emojiAriaLabel);
    this.el.attachBtn.setAttribute('aria-label', this.t.attachAriaLabel);
    this.el.sendBtn.setAttribute('aria-label', this.t.sendButton);
    // 评分面板（若已 markRated 或显示 thanks 不动）
    const rp = this.el.ratingPanel;
    const t1 = rp.querySelector<HTMLElement>('.leke-rating-title');
    if (t1) t1.textContent = this.t.ratingTitle;
    const p1 = rp.querySelector<HTMLElement>('.leke-rating-prompt');
    if (p1) p1.textContent = this.t.ratingPrompt;
    const cm = rp.querySelector<HTMLTextAreaElement>('.leke-rating-comment');
    if (cm) cm.placeholder = this.t.ratingCommentPlaceholder;
    const sk = rp.querySelector<HTMLElement>('.leke-rating-skip');
    if (sk) sk.textContent = this.t.ratingSkip;
    const sb = rp.querySelector<HTMLElement>('.leke-rating-submit');
    if (sb) sb.textContent = this.t.ratingSubmit;
  }

  /** 由 lekechat.ts 在拿到 visitor auth 响应后调用：注入服务端配置的浮窗标题。
   *  非破坏性，仍受 opts.title 覆盖。 */
  setTitle(t: string | null | undefined) {
    this.serverTitle = (t ?? '').trim();
    const titleEl = this.el.header.querySelector<HTMLElement>('.leke-header-title');
    if (titleEl) titleEl.textContent = this.resolveTitle();
  }

  /** 由 lekechat.ts 在拿到 visitor auth 响应后调用：注入租户品牌 logo。
   *  两处展示：顶部标题前的小图标、坐席消息气泡左侧的头像。空 = 两处都不展示。 */
  setLogo(url: string | null | undefined): void {
    const next = (url ?? '').trim();
    if (next === this.logoUrl) return;
    this.logoUrl = next;
    const img = this.el.header.querySelector<HTMLImageElement>('.leke-header-logo');
    if (img) {
      if (next) {
        img.src = next;
        img.style.display = '';
      } else {
        img.removeAttribute('src');
        img.style.display = 'none';
      }
    }
    // 已渲染的坐席气泡也跟着变（每条 peer 消息都预留了头像位，这里只切 src / 显隐）
    this.el.messages.querySelectorAll<HTMLImageElement>('.leke-msg-avatar').forEach((av) => {
      if (next) {
        av.src = next;
        av.style.display = '';
      } else {
        av.removeAttribute('src');
        av.style.display = 'none';
      }
    });
  }

  /** 三级 fallback：opts.title > 服务端 widgetTitle > 当前 locale 的 i18n 默认。 */
  private resolveTitle(): string {
    if (this.opts.title) return this.opts.title;
    if (this.serverTitle) return this.serverTitle;
    return this.t.title;
  }

  destroy() {
    this.unsubscribe();
    this.clearPreviewTimer();
    if (this.outsideClickHandler) {
      document.removeEventListener('mousedown', this.outsideClickHandler, true);
      document.removeEventListener('touchstart', this.outsideClickHandler, true);
      this.outsideClickHandler = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.viewportHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.viewportHandler);
      window.visualViewport.removeEventListener('scroll', this.viewportHandler);
      this.viewportHandler = null;
    }
    this.unlockHostScroll();
    this.el.launcher.remove();
    this.el.panel.remove();
    this.el.backdrop.remove();
  }

  private injectStyle() {
    const id = 'leke-widget-style';
    if (document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = widgetCss;
    document.head.appendChild(s);
  }

  private buildDom() {
    const launcher = document.createElement('button');
    launcher.className = 'leke-launcher';
    launcher.setAttribute('aria-label', this.t.launcherAriaLabel);
    launcher.setAttribute('data-unread', '0');
    // 宿主用自己的按钮触发展开时，隐藏自带悬浮球（面板/遮罩仍可正常展开）
    if (this.opts.hideLauncher) launcher.style.display = 'none';
    // 品牌 logo（白色版），蓝色 launcher 底色上反白显示
    launcher.innerHTML = '<svg class="leke-launcher-icon" viewBox="0 0 55 55" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M44.3978 23.7851C44.3978 20.4298 43.0649 17.2117 40.6923 14.8391C38.3197 12.4665 35.1017 11.1336 31.7463 11.1336H23.2554C21.594 11.1336 19.9488 11.4608 18.4138 12.0965C16.8789 12.7323 15.4841 13.6643 14.3093 14.8391C13.1345 16.0139 12.2026 17.4086 11.5668 18.9436C10.931 20.4785 10.6038 22.1238 10.6038 23.7851C10.6038 28.8604 13.2463 32.7646 17.6569 36.1305C20.5272 38.321 23.9364 40.1153 27.4159 41.6827V36.4365H31.7463C35.1016 36.4365 38.3197 35.1036 40.6923 32.731C43.0649 30.3584 44.3978 27.1405 44.3978 23.7851ZM48.7267 23.9961C48.6718 28.4236 46.8892 32.658 43.7543 35.7929L43.6041 35.9412C40.4344 39.0331 36.1798 40.7668 31.7463 40.7668V48.1965C21.2155 43.9842 6.50534 37.6822 6.27617 24.107L6.27344 23.7851C6.27344 21.5551 6.71257 19.3467 7.56598 17.2864C8.39272 15.2905 9.59256 13.4713 11.1003 11.9259L11.2474 11.7771C12.8243 10.2002 14.6963 8.94918 16.7566 8.09577C18.7525 7.26904 20.8873 6.83105 23.0462 6.80447L23.2554 6.80322H31.7463L31.9572 6.80447C36.3848 6.8594 40.6193 8.64217 43.7543 11.7771C46.939 14.9618 48.7282 19.2813 48.7282 23.7851L48.7267 23.9961Z" fill="white"/><path d="M34.6715 24.2496C34.6704 24.2512 34.669 24.2531 34.6676 24.2551C34.6668 24.2561 34.6659 24.2572 34.6651 24.2583C34.6647 24.2589 34.664 24.2598 34.6638 24.2601C34.6638 24.2607 34.671 24.2665 34.884 24.4175L37.9775 26.6056C38.1983 26.7622 38.1978 26.7628 38.1974 26.7633C38.1972 26.7637 38.1966 26.7647 38.1962 26.7653C38.1952 26.7667 38.194 26.7681 38.193 26.7695C38.1909 26.7725 38.1885 26.7758 38.186 26.7792C38.1811 26.7861 38.1755 26.7939 38.1691 26.8026C38.1563 26.8201 38.1405 26.8413 38.1218 26.8658C38.0845 26.9149 38.0352 26.9776 37.9741 27.0519C37.8518 27.2004 37.6807 27.3963 37.4589 27.623C37.0158 28.0758 36.3652 28.6569 35.4907 29.2315C33.7264 30.3906 31.086 31.4977 27.5001 31.4977C23.9142 31.4977 21.2738 30.3906 19.5095 29.2315C18.635 28.6569 17.9844 28.0758 17.5413 27.623C17.3195 27.3963 17.1484 27.2004 17.0261 27.0519C16.965 26.9776 16.9157 26.9149 16.8784 26.8658C16.8597 26.8413 16.8439 26.8201 16.8311 26.8026C16.8248 26.7939 16.8191 26.7861 16.8142 26.7792C16.8117 26.7758 16.8094 26.7725 16.8072 26.7695C16.8062 26.7681 16.805 26.7667 16.804 26.7653C16.8036 26.7647 16.8031 26.7637 16.8028 26.7633C16.8024 26.7628 16.8019 26.7622 17.0227 26.6056L20.1162 24.4175C20.3292 24.2665 20.3364 24.2607 20.3364 24.2601C20.3362 24.2598 20.3355 24.2589 20.3351 24.2583C20.3343 24.2572 20.3334 24.2561 20.3326 24.2551C20.3312 24.2531 20.3298 24.2512 20.3287 24.2496L20.3242 24.2432C20.3295 24.2501 20.3444 24.2695 20.3692 24.2996C20.419 24.3601 20.5077 24.4627 20.6364 24.5942C20.8944 24.8578 21.3081 25.232 21.8874 25.6126C23.0309 26.3639 24.856 27.1673 27.5001 27.1673C30.1442 27.1673 31.9693 26.3639 33.1128 25.6126C33.6921 25.232 34.1059 24.8578 34.3638 24.5942C34.4925 24.4627 34.5813 24.3601 34.631 24.2996C34.6558 24.2695 34.6707 24.2501 34.676 24.2432L34.6715 24.2496Z" fill="white"/></svg>';
    // 从 localStorage 恢复上次位置（仅垂直，水平始终吸右）
    try {
      const saved = localStorage.getItem('leke.launcher.bottom');
      if (saved) {
        const b = Math.max(8, Math.min(parseInt(saved, 10), window.innerHeight - 64));
        if (!isNaN(b)) launcher.style.bottom = b + 'px';
      }
    } catch { /* ignore */ }

    const panel = document.createElement('div');
    panel.className = 'leke-panel';
    panel.setAttribute('data-open', 'false');

    const header = document.createElement('div');
    header.className = 'leke-header';
    // 品牌区：logo（租户配置了才显示）+ 标题/状态
    const brand = document.createElement('div');
    brand.className = 'leke-header-brand';
    const headerLogo = document.createElement('img');
    headerLogo.className = 'leke-header-logo';
    headerLogo.alt = '';
    headerLogo.style.display = 'none';
    const titleBox = document.createElement('div');
    const title = document.createElement('div');
    title.className = 'leke-header-title';
    title.textContent = this.resolveTitle();
    // 状态行（连接中/在线/断开）保留 DOM 节点便于调试，但默认隐藏——产品决定不向访客展示。
    const headerStatus = document.createElement('div');
    headerStatus.className = 'leke-header-status';
    headerStatus.style.display = 'none';
    headerStatus.textContent = this.t.statusConnecting;
    titleBox.appendChild(title);
    titleBox.appendChild(headerStatus);
    const headerActions = document.createElement('div');
    headerActions.className = 'leke-header-actions';
    const endBtn = document.createElement('button');
    endBtn.className = 'leke-header-end';
    endBtn.type = 'button';
    endBtn.title = this.t.endConversation;
    endBtn.textContent = this.t.endConversation;
    if (!this.opts.endConversation) endBtn.style.display = 'none';
    const closeBtn = document.createElement('button');
    closeBtn.className = 'leke-header-close';
    closeBtn.textContent = '×';
    closeBtn.setAttribute('aria-label', this.t.closeAriaLabel);
    headerActions.appendChild(endBtn);
    headerActions.appendChild(closeBtn);
    brand.appendChild(headerLogo);
    brand.appendChild(titleBox);
    header.appendChild(brand);
    header.appendChild(headerActions);

    const messages = document.createElement('div');
    messages.className = 'leke-messages';

    const inputWrap = document.createElement('div');
    inputWrap.className = 'leke-input';
    const textarea = document.createElement('textarea');
    textarea.placeholder = this.t.textareaPlaceholder;
    textarea.rows = 1;
    // emoji 按钮（按产品要求：访客端暂时隐藏 emoji 入口，逻辑与面板保留）
    const emojiBtn = document.createElement('button');
    emojiBtn.className = 'leke-emoji-btn';
    emojiBtn.setAttribute('aria-label', this.t.emojiAriaLabel);
    emojiBtn.textContent = '😊';
    emojiBtn.type = 'button';
    emojiBtn.style.display = 'none';
    // emoji 面板
    const emojiPanel = this.buildEmojiPanel();
    // 附件：单一「+」入口，选任意文件后按 MIME 自动识别图片/视频/文件
    const attachBtn = document.createElement('button');
    attachBtn.className = 'leke-attach';
    attachBtn.setAttribute('aria-label', this.t.attachAriaLabel);
    attachBtn.innerHTML = ICON_PLUS;
    attachBtn.type = 'button';
    if (!this.opts.uploadFile) attachBtn.disabled = true;
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.style.display = 'none';
    const sendBtn = document.createElement('button');
    sendBtn.className = 'leke-send';
    sendBtn.innerHTML = ICON_SEND;
    sendBtn.setAttribute('aria-label', this.t.sendButton);
    sendBtn.type = 'button';
    sendBtn.disabled = true;
    inputWrap.appendChild(emojiBtn);
    inputWrap.appendChild(attachBtn);
    inputWrap.appendChild(fileInput);
    inputWrap.appendChild(textarea);
    inputWrap.appendChild(sendBtn);

    const ratingPanel = this.buildRatingPanel();

    const hint = document.createElement('div');
    hint.className = 'leke-input-hint';
    hint.textContent = `0 / ${this.opts.textLimit}`;
    hint.style.display = 'none';

    const queueBar = document.createElement('div');
    queueBar.className = 'leke-queue-bar';
    queueBar.style.display = 'none';

    panel.appendChild(header);
    panel.appendChild(queueBar);
    panel.appendChild(messages);
    panel.appendChild(ratingPanel);
    panel.appendChild(emojiPanel);
    panel.appendChild(inputWrap);
    panel.appendChild(hint);

    // 半透明遮罩：panel 打开后覆盖宿主页面，仅用于拦截点击 + 视觉聚焦。
    // 点击遮罩本身不关闭浮窗 —— 关闭必须用 panel header 的 × 按钮（产品要求）。
    const backdrop = document.createElement('div');
    backdrop.className = 'leke-backdrop';

    document.body.appendChild(launcher);
    document.body.appendChild(backdrop);
    document.body.appendChild(panel);

    return {
      launcher,
      panel,
      backdrop,
      header,
      headerStatus,
      messages,
      textarea,
      sendBtn,
      hint,
      attachBtn,
      fileInput,
      endBtn,
      ratingPanel,
      emojiBtn,
      emojiPanel,
      queueBar,
    };
  }

  // buildEmojiPanel：分组的 emoji 选择面板。点击任意 emoji 插入到 textarea 当前光标位置。
  private buildEmojiPanel(): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'leke-emoji-panel';
    wrap.style.display = 'none';
    for (const group of EMOJI_GROUPS) {
      const row = document.createElement('div');
      row.className = 'leke-emoji-row';
      for (const ch of group.chars) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'leke-emoji-item';
        b.textContent = ch;
        b.addEventListener('click', () => this.insertEmoji(ch));
        row.appendChild(b);
      }
      wrap.appendChild(row);
    }
    return wrap;
  }

  private insertEmoji(ch: string) {
    const ta = this.el.textarea;
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    ta.value = ta.value.slice(0, start) + ch + ta.value.slice(end);
    const pos = start + ch.length;
    ta.focus();
    ta.setSelectionRange(pos, pos);
    // 手动触发 input 事件让 onInputChange 更新字数 + sendBtn 状态
    ta.dispatchEvent(new Event('input', { bubbles: true }));
    // 选中后自动关闭面板
    this.hideEmojiPanel();
  }

  private toggleEmojiPanel() {
    const visible = this.el.emojiPanel.style.display !== 'none';
    if (visible) this.hideEmojiPanel();
    else this.showEmojiPanel();
  }

  private showEmojiPanel() {
    this.el.emojiPanel.style.display = 'block';
    // 装上 document 级监听以捕获外部点击；用 capture 让 emoji 按钮的 click 先到 insertEmoji
    if (!this.outsideClickHandler) {
      this.outsideClickHandler = (ev: Event) => {
        const t = ev.target as Node | null;
        if (!t) return;
        if (this.el.emojiPanel.contains(t) || this.el.emojiBtn.contains(t)) return;
        this.hideEmojiPanel();
      };
      document.addEventListener('mousedown', this.outsideClickHandler, true);
      document.addEventListener('touchstart', this.outsideClickHandler, true);
    }
  }

  private hideEmojiPanel() {
    this.el.emojiPanel.style.display = 'none';
    if (this.outsideClickHandler) {
      document.removeEventListener('mousedown', this.outsideClickHandler, true);
      document.removeEventListener('touchstart', this.outsideClickHandler, true);
      this.outsideClickHandler = null;
    }
  }

  // buildRatingPanel：5 星 + 评论输入框 + 提交/跳过。整体默认隐藏。
  private buildRatingPanel(): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'leke-rating';
    wrap.style.display = 'none';

    const title = document.createElement('div');
    title.className = 'leke-rating-title';
    title.textContent = this.t.ratingTitle;

    const prompt = document.createElement('div');
    prompt.className = 'leke-rating-prompt';
    prompt.textContent = this.t.ratingPrompt;

    const stars = document.createElement('div');
    stars.className = 'leke-rating-stars';
    const starBtns: HTMLButtonElement[] = [];
    for (let i = 1; i <= 5; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'leke-rating-star';
      b.textContent = '☆';
      b.dataset.value = String(i);
      b.addEventListener('mouseenter', () => this.paintStars(starBtns, i));
      b.addEventListener('mouseleave', () => this.paintStars(starBtns, this.currentStars));
      b.addEventListener('click', () => {
        this.currentStars = i;
        this.paintStars(starBtns, i);
      });
      starBtns.push(b);
      stars.appendChild(b);
    }

    const comment = document.createElement('textarea');
    comment.className = 'leke-rating-comment';
    comment.rows = 2;
    comment.placeholder = this.t.ratingCommentPlaceholder;

    const actions = document.createElement('div');
    actions.className = 'leke-rating-actions';
    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'leke-rating-skip';
    skip.textContent = this.t.ratingSkip;
    skip.addEventListener('click', () => this.hideRatingPanel());
    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'leke-rating-submit';
    submit.textContent = this.t.ratingSubmit;
    submit.addEventListener('click', () => void this.onRatingSubmit(comment));
    actions.appendChild(skip);
    actions.appendChild(submit);

    wrap.appendChild(title);
    wrap.appendChild(prompt);
    wrap.appendChild(stars);
    wrap.appendChild(comment);
    wrap.appendChild(actions);
    return wrap;
  }

  private paintStars(btns: HTMLButtonElement[], n: number) {
    for (let i = 0; i < btns.length; i++) {
      btns[i].textContent = i < n ? '★' : '☆';
      btns[i].classList.toggle('on', i < n);
    }
  }

  private async onRatingSubmit(commentEl: HTMLTextAreaElement) {
    if (!this.opts.submitRating || this.rated) return;
    if (this.currentStars < 1) return;
    try {
      await this.opts.submitRating(this.currentStars, commentEl.value.trim());
      this.rated = true;
      // 替换为感谢提示，2.5s 后隐藏
      this.el.ratingPanel.innerHTML = '';
      const thanks = document.createElement('div');
      thanks.className = 'leke-rating-thanks';
      thanks.textContent = this.t.ratingThanks;
      this.el.ratingPanel.appendChild(thanks);
      window.setTimeout(() => this.hideRatingPanel(), 2500);
    } catch (err) {
      this.addMessage({
        id: 'err-rating-' + Date.now(),
        side: 'system',
        text: this.t.ratingSubmitFailed(String(err)),
        ts: Date.now(),
      });
    }
  }

  private showRatingPanel() {
    if (this.rated) return;
    if (!this.opts.submitRating) return;
    if (this.opts.ratingFeatureEnabled === false) return;
    this.el.ratingPanel.style.display = 'block';
  }

  /** 标记该会话已有历史评分，避免再次弹评价面板。
   *  当 AiKeChat 复用已有会话且该会话已被评过时调用。 */
  markRated(): void {
    this.rated = true;
    this.hideRatingPanel();
  }

  /** 由 AiKeChat 在 auth 完成后调用：透传客户级评价开关。
   *  关闭时即便会话结束也不弹评价面板。 */
  setRatingFeatureEnabled(enabled: boolean): void {
    this.opts.ratingFeatureEnabled = enabled;
    if (!enabled) this.hideRatingPanel();
  }

  /** 设置客户级已读回执开关并立即按新值重渲已读显示。
   *  bootstrap 时由 AiKeChat 注入初值;运行中由服务端 read_receipt_changed 通知实时变更。 */
  setReadReceiptEnabled(enabled: boolean): void {
    this.opts.readReceiptEnabled = enabled;
    this.applyReadDisplay();
  }

  /** 由 AiKeChat 在 auth 完成后调用：透传客户级「坐席可见输入预览」开关。
   *  关闭时不再上行 preview,已在途的 debounce 也一并取消。 */
  setTypingPreviewEnabled(enabled: boolean): void {
    this.opts.typingPreviewEnabled = enabled;
    if (!enabled) this.clearPreviewTimer();
  }

  private hideRatingPanel() {
    this.el.ratingPanel.style.display = 'none';
  }

  private bindEvents() {
    this.bindLauncherDrag();
    this.el.header.querySelector('.leke-header-close')!.addEventListener('click', () =>
      this.toggle(false),
    );

    this.el.textarea.addEventListener('input', () => this.onInputChange());
    this.el.textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void this.onSendClick();
      }
    });
    // 移动端点 <button> 会把焦点从 textarea 抢走 → iOS 收起软键盘,连带触发一次
    // 键盘收/弹切换(空白偶发的主要来源)。在 mousedown 上 preventDefault 阻止焦点
    // 转移,textarea 始终保持焦点,发送后键盘常驻不收。Enter 发送路径本就不失焦。
    this.el.sendBtn.addEventListener('mousedown', (e) => e.preventDefault());
    this.el.sendBtn.addEventListener('click', () => void this.onSendClick());
    this.el.attachBtn.addEventListener('click', () => {
      if (!this.opts.uploadFile) return;
      this.el.fileInput.value = '';
      this.el.fileInput.click();
    });
    this.el.fileInput.addEventListener('change', (ev) => {
      const f = (ev.target as HTMLInputElement).files?.[0];
      if (f) void this.onFilePicked(f);
    });
    this.el.endBtn.addEventListener('click', () => void this.onEndClick());
    this.el.emojiBtn.addEventListener('click', () => this.toggleEmojiPanel());
  }

  // bindLauncherDrag：launcher 始终吸在屏幕右侧，垂直方向可拖；
  // 用拖动距离判断是 click 还是 drag（< 5px 视为 click，打开浮窗）。
  private bindLauncherDrag() {
    const launcher = this.el.launcher;
    let startY = 0;
    let startBottom = 0;
    let dragged = false;
    let active = false;

    const onMove = (ev: PointerEvent) => {
      if (!active) return;
      const dy = ev.clientY - startY;
      if (Math.abs(dy) > 5) dragged = true;
      // bottom 与 clientY 反向：clientY 增加 → bottom 减少
      const newBottom = Math.max(
        8,
        Math.min(window.innerHeight - 64, startBottom - dy),
      );
      launcher.style.bottom = newBottom + 'px';
    };
    const onUp = (ev: PointerEvent) => {
      if (!active) return;
      active = false;
      launcher.releasePointerCapture(ev.pointerId);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (dragged) {
        try {
          const b = parseInt(launcher.style.bottom, 10);
          if (!isNaN(b)) localStorage.setItem('leke.launcher.bottom', String(b));
        } catch { /* ignore */ }
      } else {
        // 没拖动 → 当作 click，打开浮窗
        this.toggle(true);
      }
    };
    launcher.addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      active = true;
      dragged = false;
      startY = ev.clientY;
      const cs = getComputedStyle(launcher);
      startBottom = parseInt(cs.bottom, 10) || 24;
      launcher.setPointerCapture(ev.pointerId);
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    });
    // 兜底:embed/chat.html 独占视口模式靠 launcher.click() 编程模拟开窗,
    // pointerdown/pointerup 序列不走,this.open 一直停 false。后果:访客收到
    // 对端消息时 case 'message' 里 if (this.open) maybeSendRead() 拦掉,
    // 访客端永不发 msg.read,坐席端"已送达"永远变不成"已读"。
    // 真实鼠标 click 时 pointerup 已 toggle 过,这里 idempotent noop。
    launcher.addEventListener('click', () => {
      if (!this.open) this.toggle(true);
    });
  }

  private async onEndClick() {
    if (this.ended || !this.opts.endConversation) return;
    if (!window.confirm(this.t.endConfirm)) return;
    // 先置位再发请求：后端回的 conv_ended 帧可能抢在 HTTP 响应之前到达，
    // 那时 this.ended 还是 false，会误插一条「坐席已结束对话」。
    this.endedByMe = true;
    try {
      await this.opts.endConversation();
      this.markEnded();
    } catch (err) {
      this.endedByMe = false; // 没结束成，恢复
      this.addMessage({
        id: 'err-end-' + Date.now(),
        side: 'system',
        text: this.t.genericError(String(err)),
        ts: Date.now(),
      });
    }
  }

  /** 标记会话已结束：禁用输入 / 上传 / 结束按钮，并提示一条系统消息。
   *  幂等：重复调用只渲染一次提示。 */
  markEnded(): void {
    if (this.ended) return;
    this.ended = true;
    this.el.textarea.disabled = true;
    this.el.sendBtn.disabled = true;
    this.el.attachBtn.disabled = true;
    this.el.endBtn.disabled = true;
    this.addMessage({
      id: 'sys-ended',
      side: 'system',
      text: this.t.endedNotice,
      ts: Date.now(),
    });
    this.showRatingPanel();
    this.showRestartButton();
  }

  /** 会话结束后，在消息区底部渲染一个"重新开始对话"小按钮。
   *  旧对话记录原样保留，不清空；幂等：重复调用只渲染一次。 */
  private showRestartButton(): void {
    if (!this.opts.restartConversation || this.restartRow) return;
    const row = document.createElement('div');
    row.className = 'leke-msg-wrap leke-msg-wrap-sys leke-restart-row';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'leke-restart-btn';
    btn.textContent = this.t.restartConversation;
    btn.addEventListener('click', () => void this.onRestartClick());
    row.appendChild(btn);
    this.el.messages.appendChild(row);
    this.el.messages.scrollTop = this.el.messages.scrollHeight;
    this.restartRow = row;
  }

  private async onRestartClick(): Promise<void> {
    if (!this.opts.restartConversation) return;
    try {
      await this.opts.restartConversation();
    } catch (err) {
      this.addMessage({
        id: 'err-restart-' + Date.now(),
        side: 'system',
        text: this.t.genericError(String(err)),
        ts: Date.now(),
      });
      return;
    }
    this.resetEndedUI();
  }

  // 恢复到可继续输入状态；旧对话记录保留，仅移除结束提示与「重新开始对话」按钮。
  // 幂等。点按钮重启 / 已结束态直接发消息自动重启 两条路径共用。
  private resetEndedUI(): void {
    this.ended = false;
    this.endedByMe = false;
    this.rated = false;
    this.el.textarea.disabled = false;
    this.el.sendBtn.disabled = false;
    this.el.attachBtn.disabled = !this.opts.uploadFile;
    this.el.endBtn.disabled = false;
    this.hideRatingPanel();
    const endedNode = this.el.messages.querySelector<HTMLDivElement>(
      `[data-msg-id="${cssEscape('sys-ended')}"]`,
    );
    if (endedNode) endedNode.remove();
    this.msgs.delete('sys-ended');
    // 「坐席已结束对话…」与「会话因长时间无操作结束…」提示不立即抹掉，
    // 延迟 3s 再移除，避免视觉跳变
    const endedByAgentNode = this.el.messages.querySelector<HTMLDivElement>(
      `[data-msg-id="${cssEscape('sys-ended-by-agent')}"]`,
    );
    if (endedByAgentNode) {
      window.setTimeout(() => {
        endedByAgentNode.remove();
        this.msgs.delete('sys-ended-by-agent');
      }, 3000);
    }
    const endedByIdleNode = this.el.messages.querySelector<HTMLDivElement>(
      `[data-msg-id="${cssEscape('sys-ended-by-idle')}"]`,
    );
    if (endedByIdleNode) {
      window.setTimeout(() => {
        endedByIdleNode.remove();
        this.msgs.delete('sys-ended-by-idle');
      }, 3000);
    }
    if (this.restartRow) {
      this.restartRow.remove();
      this.restartRow = null;
    }
  }

  // 已结束态下访客直接发文本/附件时，自动跑一遍 restart 流程，省掉「先点按钮」一步。
  // 返回 true = 已就绪可继续发送；false = 没配 restartConversation 或 restart 失败，调用方应中止。
  private async autoRestartIfEnded(): Promise<boolean> {
    if (!this.ended) return true;
    if (!this.opts.restartConversation) return false;
    try {
      await this.opts.restartConversation();
    } catch (err) {
      this.addMessage({
        id: 'err-restart-' + Date.now(),
        side: 'system',
        text: this.t.genericError(String(err)),
        ts: Date.now(),
      });
      return false;
    }
    this.resetEndedUI();
    return true;
  }

  /** 在浮窗最前面渲染一条欢迎语，样式与坐席正常发言一致（peer 气泡 + 发送者名 + 时间）；
   *  仅本地展示，不入库、不发往服务端，因此标 local（不可引用）。
   *  幂等：重复调用只展示一次（id 固定）。 */
  showWelcome(message: string): void {
    if (!message) return;
    if (this.msgs.has('sys-welcome')) return;
    this.addMessage({
      id: 'sys-welcome',
      side: 'peer',
      senderName: this.agentName || this.t.senderAgent,
      text: message,
      ts: Date.now(),
      local: true,
    });
  }

  /** 欢迎语通常在坐席接线前就渲染好了（那时只能显示默认「客服」名）；
   *  拿到真实坐席名后回填一次，避免同一个人在欢迎语与后续消息里显示成两个名字。 */
  private syncWelcomeSenderName(): void {
    const v = this.msgs.get('sys-welcome');
    if (!v || !this.agentName || v.senderName === this.agentName) return;
    v.senderName = this.agentName;
    const nameEl = this.el.messages.querySelector(
      '[data-msg-id="sys-welcome"] .leke-msg-sender',
    );
    if (nameEl) nameEl.textContent = this.agentName;
  }

  /** 加载历史消息（按时间正序传入；服务端返回 DESC，调用方需 reverse）。
   *  默认在第一次连接成功前调用，不计入 unread；重复 id 自动去重。 */
  /** 加载/合并当前会话历史，按 msgId 幂等去重。
   *  返回本次「新增的」peer 侧消息，供重连补拉判断未读/响铃（普通调用方可忽略返回值）。 */
  loadHistory(items: HistoryItem[]): MessageView[] {
    const newPeers: MessageView[] = [];
    for (const it of items) {
      if (this.msgs.has(it.id)) continue;
      // 撤回的消息：访客端直接不展示（不留"[消息已撤回]"占位）
      if (it.recalled) continue;
      const view = this.historyView(it);
      if (view.side === 'peer' && it.id > this.latestPeerMsgId) this.latestPeerMsgId = it.id;
      this.appendDaySeparator(view.ts);
      this.msgs.set(view.id, view);
      this.el.messages.appendChild(this.renderMessage(view));
      if (view.side === 'peer') newPeers.push(view);
    }
    this.trimMessages();
    this.el.messages.scrollTop = this.el.messages.scrollHeight;
    return newPeers;
  }

  /** 重连补拉专用：合并断连期间的历史后，对新到的坐席消息补未读角标 / 响铃 / 通知。
   *  与实时 message 分支同源，区别是这里可能一次补多条；首次进线的历史加载直接走
   *  loadHistory、不触发提醒。 */
  syncReconnectHistory(items: HistoryItem[]): void {
    const newPeers = this.loadHistory(items);
    if (newPeers.length === 0) return;
    if (this.open) {
      // 浮窗开着：把已读推进到最新，坐席侧「已读」正常流转
      this.maybeSendRead();
      return;
    }
    // 浮窗关着：累加未读 + 响铃一次 + 通知（取最后一条做预览）
    this.unread += newPeers.length;
    this.el.launcher.setAttribute('data-unread', String(this.unread));
    if (!shouldNotify(this.open)) return;
    if (this.opts.soundEnabled !== false) playBeep();
    if (this.opts.notificationEnabled !== false) {
      const preview = this.previewOf(newPeers[newPeers.length - 1]);
      void showNotification(
        this.resolveTitle(),
        preview,
        'leke-' + (this.opts.getConvId() || 'msg'),
        () => this.toggle(true),
      );
    }
  }

  /** 未读通知的文本预览：附件转成 [图片]/[视频]/[文件]，文本截断 80 字。 */
  private previewOf(v: MessageView): string {
    if (v.attachment) {
      return v.attachment.kind === 'image'
        ? this.t.mediaImage
        : v.attachment.kind === 'video'
          ? this.t.mediaVideo
          : this.t.mediaFile;
    }
    return (v.text ?? '').slice(0, 80);
  }

  /** HistoryItem → MessageView 纯映射（不触读取标记/不入 DOM），
   *  当前会话历史 loadHistory 与跨会话折叠历史区共用。 */
  private historyView(it: HistoryItem): MessageView {
    const side: 'self' | 'peer' = it.senderType === 'visitor' ? 'self' : 'peer';
    const ts = new Date(it.createdAt).getTime();
    // 显示名：坐席消息（含协同坐席）一律显示为当前会话坐席，访客无感；
    // bot/system 仍按后端 senderName 优先、否则按类型默认。
    let senderName = it.senderName || '';
    if (side === 'peer') {
      if (it.senderType === 'agent') senderName = this.agentName || this.t.senderAgent;
      else if (!senderName) {
        if (it.senderType === 'bot') senderName = this.t.senderBot;
        else if (it.senderType === 'system') senderName = this.t.senderSystem;
      }
    }
    const quote = this.quoteFromHistory(it.quote);
    if (it.contentType === 'image' || it.contentType === 'video' || it.contentType === 'file') {
      return {
        id: it.id,
        side,
        senderName,
        attachment: { kind: it.contentType as AttachmentKind, meta: it.content as AttachmentMeta },
        ts,
        state: side === 'self' ? 'sent' : undefined,
        quote,
      };
    }
    return {
      id: it.id,
      side,
      senderName,
      text: String(it.content ?? ''),
      ts,
      state: side === 'self' ? 'sent' : undefined,
      quote,
    };
  }

  // ===== 跨会话历史对话折叠区（同源站ID/customer，点击展开、before 游标分页）=====
  private histPager: HistoryPager | null = null;
  private histZoneEl: HTMLDivElement | null = null;
  private histListEl: HTMLDivElement | null = null;
  private histToggleBtn: HTMLButtonElement | null = null;
  private histMoreBtn: HTMLButtonElement | null = null;
  private histExpanded = false;
  private histLoading = false;
  private histHasMore = false;
  // 已加载的历史（升序），每页前插；重渲以正确画会话分隔
  private histViews: { view: MessageView; convId: string }[] = [];

  /** 启用「查看历史对话」折叠区（老访客/已识别时由调用方注入分页器）。幂等。 */
  enableHistory(pager: HistoryPager): void {
    this.histPager = pager;
    if (this.histZoneEl) return;
    const zone = document.createElement('div');
    // 注意：不带 leke-msg-wrap —— 否则 .leke-msg-wrap:hover .leke-quote-btn
    // 会因 hover 冒泡到容器而让区内所有消息同时浮出引用按钮。
    zone.className = 'leke-hist-zone';
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'leke-hist-toggle';
    toggle.textContent = this.t.historyView;
    toggle.addEventListener('click', () => void this.onHistToggle());
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'leke-hist-toggle leke-hist-more';
    more.textContent = this.t.historyMore;
    more.style.display = 'none';
    more.addEventListener('click', () => void this.loadHistPage(true));
    const list = document.createElement('div');
    list.className = 'leke-hist-list';
    list.style.display = 'none';
    zone.appendChild(toggle);
    zone.appendChild(more);
    zone.appendChild(list);
    this.histZoneEl = zone;
    this.histListEl = list;
    this.histToggleBtn = toggle;
    this.histMoreBtn = more;
    // 置于消息区最顶部，历史永远在当前会话之上
    this.el.messages.insertBefore(zone, this.el.messages.firstChild);
  }

  private async onHistToggle(): Promise<void> {
    if (this.histExpanded) return;
    this.histExpanded = true;
    if (this.histToggleBtn) this.histToggleBtn.style.display = 'none';
    if (this.histListEl) this.histListEl.style.display = '';
    await this.loadHistPage(false);
  }

  private async loadHistPage(older: boolean): Promise<void> {
    if (!this.histPager || this.histLoading) return;
    if (older && (!this.histHasMore || this.histViews.length === 0)) return;
    this.histLoading = true;
    const box = this.el.messages;
    const prevH = box.scrollHeight;
    const prevTop = box.scrollTop;
    const btn = older ? this.histMoreBtn : this.histToggleBtn;
    if (btn) btn.textContent = this.t.historyLoading;
    try {
      const before = older ? this.histViews[0]?.view.ts : undefined;
      const beforeISO = before ? new Date(before).toISOString() : undefined;
      const res = await this.histPager(beforeISO);
      // 后端按时间倒序；UI 需正序，更早一页前插
      const asc = res.items
        .filter((it) => !it.recalled)
        .slice()
        .reverse()
        .map((it) => ({ view: this.historyView(it), convId: it.convId || '' }));
      this.histViews = older ? [...asc, ...this.histViews] : asc;
      this.histHasMore = res.hasMore;
      this.renderHistList();
      await Promise.resolve();
      // 历史在当前消息之上前插：补偿新增高度，视图停在原来那条消息，
      // 不跳到第一条（首次展开同理）。
      box.scrollTop = prevTop + (box.scrollHeight - prevH);
    } catch (e) {
      console.warn('[leke] history page failed:', e);
    } finally {
      this.histLoading = false;
      if (this.histMoreBtn) {
        this.histMoreBtn.textContent = this.t.historyMore;
        this.histMoreBtn.style.display = this.histHasMore ? '' : 'none';
      }
    }
  }

  /** 从 histViews 全量重渲历史列表：相邻 convId 变化处插入会话分隔，
   *  末尾加「以上为历史对话」分隔。条目数受用户翻页驱动，量可控。 */
  private renderHistList(): void {
    const list = this.histListEl;
    if (!list) return;
    list.textContent = '';
    let prevConv: string | null = null;
    for (const { view, convId } of this.histViews) {
      if (convId !== prevConv) {
        const sep = document.createElement('div');
        sep.className = 'leke-msg-wrap leke-msg-wrap-sys';
        const s = document.createElement('div');
        s.className = 'leke-msg leke-msg-system';
        s.textContent = this.t.dateLabel(new Date(view.ts));
        sep.appendChild(s);
        list.appendChild(sep);
        prevConv = convId;
      }
      list.appendChild(this.renderMessage(view, true));
    }
    if (this.histViews.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'leke-msg-wrap leke-msg-wrap-sys';
      const s = document.createElement('div');
      s.className = 'leke-msg leke-msg-system';
      s.textContent = this.t.historyEmpty;
      empty.appendChild(s);
      list.appendChild(empty);
    } else {
      const end = document.createElement('div');
      end.className = 'leke-msg-wrap leke-msg-wrap-sys';
      const s = document.createElement('div');
      s.className = 'leke-msg leke-msg-system';
      s.textContent = this.t.historyDivider;
      end.appendChild(s);
      list.appendChild(end);
    }
  }

  private async onFilePicked(file: File) {
    if (!this.opts.uploadFile) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      this.addMessage({
        id: 'err-' + Date.now(), side: 'system',
        text: this.t.fileTooLarge(MAX_UPLOAD_MB), ts: Date.now(),
      });
      return;
    }
    // 客服结束后访客继续发附件：与文本同等处理，自动重连
    if (!(await this.autoRestartIfEnded())) return;
    if (this.opts.ensureReady) {
      try {
        await this.opts.ensureReady();
      } catch (err) {
        this.addMessage({
          id: 'err-conn-' + Date.now(), side: 'system',
          text: this.t.conversationCreateFailed(String(err)), ts: Date.now(),
        });
        return;
      }
    }
    const convId = this.opts.getConvId();
    if (!convId) {
      this.addMessage({
        id: 'err-' + Date.now(), side: 'system',
        text: this.t.sessionNotReady, ts: Date.now(),
      });
      return;
    }
    const kind: AttachmentKind = file.type.startsWith('image/')
      ? 'image'
      : file.type.startsWith('video/')
        ? 'video'
        : 'file';
    // 乐观加一条 sending 状态消息
    const tempId = 'upload-' + Date.now();
    this.addMessage({
      id: tempId, side: 'self', text: this.t.uploading(file.name),
      ts: Date.now(), state: 'sending',
    });
    try {
      const meta = await this.opts.uploadFile(file, kind);
      this.removeMessage(tempId);
      // 发送 msg.send（attachment）
      const payload: MsgSendPayload = { contentType: kind, content: meta as unknown as MsgSendPayload['content'] };
      const id = this.opts.client.send(convId, this.opts.getTo(), payload);
      this.addMessage({
        id, side: 'self', attachment: { kind, meta },
        ts: Date.now(), state: 'sending',
      });
    } catch (err) {
      this.removeMessage(tempId);
      this.addMessage({
        id: 'err-' + Date.now(), side: 'system',
        text: this.t.uploadFailed(String(err)), ts: Date.now(),
      });
    }
  }

  private removeMessage(id: string) {
    this.msgs.delete(id);
    const el = this.el.messages.querySelector(`[data-msg-id="${id}"]`);
    if (el) el.remove();
  }

  /** 展开对话面板。供宿主用自己的按钮/图标触发（AiKeChat.open()）。幂等：已展开时重复调用无副作用。 */
  openPanel(): void {
    this.toggle(true);
  }

  /** 收起对话面板。 */
  closePanel(): void {
    this.toggle(false);
  }

  private toggle(open: boolean) {
    this.open = open;
    this.el.panel.setAttribute('data-open', open ? 'true' : 'false');
    this.el.backdrop.style.display = open ? 'block' : 'none';
    if (open) {
      this.unread = 0;
      this.el.launcher.setAttribute('data-unread', '0');
      // 先锁宿主页再 focus：focus 会拉起键盘，锁在前才不给 iOS 留下可拖的滚动内容
      this.lockHostScroll();
      this.el.textarea.focus();
      // 打开浮窗时把当前最新对端消息上报为已读
      this.maybeSendRead();
    } else {
      this.unlockHostScroll();
      // 若在键盘态下关闭，清掉 viewportHandler 贴合可视视口时写的 inline 覆盖，
      // 免得残留到下次打开(下个 vv 事件前)出现错位。
      const p = this.el.panel.style;
      p.height = '';
      p.top = '';
      p.bottom = '';
      p.transform = '';
      p.setProperty('--leke-kb', '0px');
    }
  }

  // lockHostScroll：面板打开时定住宿主页。
  //
  // 浮窗是 position:fixed 盖在可滚动宿主页上。iOS 弹键盘只缩可视视口、不缩布局视口，
  // 而宿主页仍可滚，于是 iOS 允许拖动整个可视视口——一拖就把钉在布局视口底(键盘背后)
  // 的面板 padding-bottom 空白拖进可视区(大块空白 + 那条滚动条)，触摸也透传给宿主页。
  // 定住 body 后宿主页无可滚内容，iOS 不再让可视视口可拖，三个症状一并消失。
  //
  // 仅移动端全宽底栏锁：桌面(≥768)是右下角卡片、backdrop 特意放行点击让访客继续用
  // 宿主页，锁了会破坏该体验；桌面也没有软键盘，本就无此问题。
  private lockHostScroll() {
    if (this.bodyLocked || window.innerWidth >= 768) return;
    this.scrollLockY = window.scrollY;
    const b = document.body.style;
    b.position = 'fixed';
    b.top = `-${this.scrollLockY}px`;
    b.left = '0';
    b.right = '0';
    b.width = '100%';
    // 禁掉 iOS 键盘态下对可视视口的整体平移(pan)：body:fixed 只挡住了文档滚动，
    // 但键盘弹起时布局视口(660)高过可视视口(348)，iOS 仍允许把可视窗口在布局视口里
    // 上下拖(用户实测「像平移不是滚动」)，一拖面板就拖尾露出下方空区(黑底/空白)。
    // 这里拦掉 touchmove——仅当触点在可滚动的消息列表内才放行，其余一律 preventDefault，
    // 让平移手势失效。passive:false 才能 preventDefault。
    this.touchMoveBlocker = (e: TouchEvent) => {
      let node = e.target as Node | null;
      while (node && node !== this.el.panel) {
        if (node === this.el.messages) {
          const m = this.el.messages;
          if (m.scrollHeight > m.clientHeight) return; // 列表确实可滚，放行其内部滑动
          break;
        }
        node = node.parentNode;
      }
      if (e.cancelable) e.preventDefault();
    };
    document.addEventListener('touchmove', this.touchMoveBlocker, { passive: false });
    this.bodyLocked = true;
  }

  private unlockHostScroll() {
    if (!this.bodyLocked) return;
    const b = document.body.style;
    b.position = '';
    b.top = '';
    b.left = '';
    b.right = '';
    b.width = '';
    if (this.touchMoveBlocker) {
      document.removeEventListener('touchmove', this.touchMoveBlocker);
      this.touchMoveBlocker = null;
    }
    // 恢复定住前的滚动位置（定住期间 body top 负偏移把它顶到了 0）
    window.scrollTo(0, this.scrollLockY);
    this.bodyLocked = false;
  }

  private onInputChange() {
    const text = this.el.textarea.value;
    const len = Array.from(text).length;
    this.el.hint.textContent = `${len} / ${this.opts.textLimit}`;
    this.el.hint.style.color = len > this.opts.textLimit ? '#ef4444' : '#9ca3af';
    this.el.sendBtn.disabled = len === 0 || len > this.opts.textLimit;
    // 自适应高度
    this.el.textarea.style.height = 'auto';
    this.el.textarea.style.height = Math.min(100, this.el.textarea.scrollHeight) + 'px';
    // 触发 typing 通知
    if (len > 0) this.notifyTyping('start');
    else this.notifyTyping('stop');
  }

  // notifyTyping：向对端发送输入指示。
  //  - 'start' 被节流（每 2.5s 最多一条），并在 3s 后自动发 'stop'
  //  - 'stop' 立即发，并清掉节流计时
  //  - 租户开启输入预览时,'start' 改走 scheduleTypingPreview（帧里带输入框全量文本）；
  //    未开启时走原来的 2.5s 节流路径，行为与从前逐字节一致。
  private notifyTyping(state: 'start' | 'stop') {
    const to = this.opts.getTo();
    const convId = this.opts.getConvId();
    if (!to || !convId || this.ended) return;
    const now = Date.now();
    if (state === 'start') {
      // 重置自动 stop 计时
      if (this.localTypingStopTimer) window.clearTimeout(this.localTypingStopTimer);
      this.localTypingStopTimer = window.setTimeout(() => this.notifyTyping('stop'), 3000);
      if (this.opts.typingPreviewEnabled) {
        this.scheduleTypingPreview();
        return;
      }
      // 节流 start：上次发的就是 start 且距今 < 2.5s 则跳过
      if (this.lastTypingState === 'start' && now - this.lastTypingSentAt < 2500) return;
      this.lastTypingSentAt = now;
      this.lastTypingState = 'start';
      this.opts.client.sendTyping(convId, to, 'start');
    } else {
      this.clearPreviewTimer();
      if (this.localTypingStopTimer) {
        window.clearTimeout(this.localTypingStopTimer);
        this.localTypingStopTimer = null;
      }
      if (this.lastTypingState === 'stop' || this.lastTypingState === 'none') return;
      this.lastTypingState = 'stop';
      this.opts.client.sendTyping(convId, to, 'stop');
    }
  }

  // scheduleTypingPreview：停顿 300ms 后发一帧全量文本。距上次发送已超过 1s 时立即发，
  // 这同时兼顾两头——首次输入无需等 debounce 就能让坐席看到，连续打字也不会卡在旧内容上。
  private scheduleTypingPreview() {
    this.clearPreviewTimer();
    if (Date.now() - this.lastPreviewSentAt >= TYPING_PREVIEW_MAX_WAIT_MS) {
      this.flushTypingPreview();
      return;
    }
    this.previewDebounceTimer = window.setTimeout(
      () => this.flushTypingPreview(),
      TYPING_PREVIEW_DEBOUNCE_MS,
    );
  }

  // flushTypingPreview：真正发帧。debounce 到期时会话可能已切换或结束，所以这里
  // 重新取 convId/to，而不是沿用 scheduleTypingPreview 那一刻捕获的值。
  private flushTypingPreview() {
    this.clearPreviewTimer();
    const to = this.opts.getTo();
    const convId = this.opts.getConvId();
    if (!to || !convId || this.ended || !this.opts.typingPreviewEnabled) return;
    const text = Array.from(this.el.textarea.value).slice(0, TYPING_PREVIEW_MAX_RUNES).join('');
    if (!text) return; // 清空输入框走的是 stop 分支，这里不该发空预览
    const now = Date.now();
    this.lastPreviewSentAt = now;
    this.lastTypingSentAt = now;
    this.lastTypingState = 'start';
    this.opts.client.sendTyping(convId, to, 'start', text);
  }

  private clearPreviewTimer() {
    if (this.previewDebounceTimer) {
      window.clearTimeout(this.previewDebounceTimer);
      this.previewDebounceTimer = null;
    }
  }

  // maybeSendRead：把当前 latestPeerMsgId 作为已读位置上报。
  //  - 仅在浮窗打开 + 标签页可见时调用
  //  - 与 lastReadSent 比较去重（同一个 id 不重复发）
  private maybeSendRead() {
    if (!this.latestPeerMsgId) return;
    if (this.latestPeerMsgId === this.lastReadSent) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const convId = this.opts.getConvId();
    if (!convId) return;
    this.lastReadSent = this.latestPeerMsgId;
    this.opts.client.sendRead(convId, this.latestPeerMsgId);
  }

  // 坐席最新已读位置（msg.read 帧 / 历史回放更新）。实时全局语义下,已读显示 =
  // 「开关开 且 该 self 消息 ≤ 此位置」,翻开关时据此整体重算(applyReadDisplay)。
  private peerReadMsgId = '';

  // applyPeerRead：记录坐席已读位置并按当前开关重算已读显示。
  private applyPeerRead(lastReadMsgId: string) {
    if (!lastReadMsgId) return;
    this.peerReadMsgId = lastReadMsgId;
    this.applyReadDisplay();
  }

  // applyReadDisplay：按「当前开关 + 坐席已读位置」整体重算所有 self 消息的 已读/已送达。
  // 开关开→读过的升「已读」;开关关→全部回「已送达」。开关由服务端实时变更后调用即可
  // 无刷新生效。定位 lastReadMsgId 用 Map 插入顺序优先、字典序兜底(同 e5dcb34)。
  private applyReadDisplay() {
    const on = this.opts.readReceiptEnabled !== false;
    const selfList: MessageView[] = [];
    for (const v of this.msgs.values()) {
      if (v.side === 'self') selfList.push(v);
    }
    const lastIdx =
      on && this.peerReadMsgId ? selfList.findIndex((v) => v.id === this.peerReadMsgId) : -1;
    for (let i = 0; i < selfList.length; i++) {
      const v = selfList[i];
      // 未确认(sending)/失败(failed)不参与已读切换;只在 已送达↔已读 间切。
      if (v.state === 'sending' || v.state === 'failed') continue;
      const isRead =
        on &&
        this.peerReadMsgId !== '' &&
        (lastIdx >= 0 ? i <= lastIdx : v.id <= this.peerReadMsgId);
      const target: 'read' | 'sent' = isRead ? 'read' : 'sent';
      if (v.state === target) continue;
      v.state = target;
      const node = this.el.messages.querySelector<HTMLDivElement>(
        `[data-msg-id="${CSS.escape(v.id)}"] .leke-msg-meta`,
      );
      if (node) node.textContent = this.stateLabel(target);
    }
  }

  private async onSendClick() {
    const text = this.el.textarea.value.trim();
    if (!text) return;
    if (Array.from(text).length > this.opts.textLimit) return;
    // 客服结束后访客继续发消息：自动走 restart（清 convId → 下面 ensureReady 会新建会话+派单）
    if (!(await this.autoRestartIfEnded())) return;

    // 延迟连接：第一次发送时才创建会话并建立 WS。
    // ensureReady 内部幂等；后续发送（已连接）开销可忽略。
    if (this.opts.ensureReady) {
      this.el.sendBtn.disabled = true;
      try {
        await this.opts.ensureReady();
      } catch (err) {
        this.el.sendBtn.disabled = false;
        this.addMessage({
          id: 'err-conn-' + Date.now(),
          side: 'system',
          text: this.t.conversationCreateFailed(String(err)),
          ts: Date.now(),
        });
        return;
      }
    }

    const convId = this.opts.getConvId();
    if (!convId) {
      this.addMessage({
        id: 'err-' + Date.now(),
        side: 'system',
        text: this.t.sessionNotReady,
        ts: Date.now(),
      });
      return;
    }

    try {
      const payload: MsgSendPayload = { contentType: 'text', content: text };
      if (this.quoting) payload.quoteOfMsgId = this.quoting.msgId;
      const id = this.opts.client.send(convId, this.opts.getTo(), payload);
      this.addMessage({
        id,
        side: 'self',
        text,
        ts: Date.now(),
        state: 'sending',
        quote: this.quoting ?? undefined,
      });
      this.clearQuote();
      this.el.textarea.value = '';
      this.onInputChange();
      this.notifyTyping('stop');
    } catch (err) {
      this.addMessage({
        id: 'err-' + Date.now(),
        side: 'system',
        text: this.t.sendFailed(String(err)),
        ts: Date.now(),
      });
    }
  }

  /** 由 AiKeChat 调：会话创建成功后通知浮窗。
   * queue 可选；非 null 表示进入排队 → 显示顶部排队条。 */
  notifyConversationReady(conv: {
    agentUserKey?: string;
    agentName?: string;
    state: string;
    queue?: { position: number; total: number } | null;
  }): void {
    if (conv.agentName) {
      this.agentName = conv.agentName;
      this.syncWelcomeSenderName();
    }
    if (conv.agentUserKey) {
      this.hideQueueBar();
    }
    if (conv.state === 'waiting' && !conv.agentUserKey) {
      // 「当前坐席繁忙，请稍候…」系统提示按产品要求暂时隐藏；
      // 排队条（queueBar）仍按位置/总数显示，不影响排队感知。
      if (conv.queue) {
        this.showQueueBar(conv.queue.position, conv.queue.total);
      }
    }
  }

  private showQueueBar(position: number, total: number): void {
    if (position <= 0) {
      this.hideQueueBar();
      return;
    }
    this.el.queueBar.textContent = this.t.queueBar(position, total);
    this.el.queueBar.style.display = 'block';
    // 进入/保持排队：启动排队心跳（30s 一次，幂等）
    const convId = this.opts.getConvId();
    if (convId) this.opts.client.startQueueHeartbeat(convId);
  }
  private hideQueueBar(): void {
    this.el.queueBar.style.display = 'none';
    // 脱队 / 接通 / 收到对端消息：停止排队心跳
    this.opts.client.stopQueueHeartbeat();
  }

  // resolveSenderName：根据消息来源 key 与类型选展示名。
  //  - bot 前缀的 key → i18n bot 名
  //  - 其余坐席（含协同坐席）统一显示为当前会话坐席名，让访客无感
  private resolveSenderName(senderKey?: string): string {
    if (!senderKey) return '';
    // bot key 形如 `{tenant}:bot`
    if (senderKey.endsWith(':bot')) return this.t.senderBot;
    // 协同坐席与主坐席对访客一视同仁：都显示为当前会话坐席
    return this.agentName || this.t.senderAgent;
  }

  /** 由 AiKeChat 调：会话创建失败时通知浮窗 */
  notifyConversationError(msg: string): void {
    this.addMessage({
      id: 'err-conv-' + Date.now(),
      side: 'system',
      text: this.t.conversationCreateFailed(msg),
      ts: Date.now(),
    });
  }

  // 断链/重连时把状态行显出来并醒目提示访客"网络不通畅"；恢复在线则重新隐藏（不打扰）。
  private applyConnStatusStyle(alert: boolean) {
    const s = this.el.headerStatus;
    s.style.display = alert ? '' : 'none';
    s.classList.toggle('leke-header-status--alert', alert);
  }

  private onClientEvent(e: ClientEvent) {
    switch (e.type) {
      case 'open':
        this.lastStatus = { kind: 'open' };
        this.el.headerStatus.textContent = this.t.statusOnline;
        this.applyConnStatusStyle(false);
        break;
      case 'close':
        this.lastStatus = { kind: 'close' };
        this.el.headerStatus.textContent = this.t.statusDisconnected;
        this.applyConnStatusStyle(true);
        break;
      case 'reconnecting':
        this.lastStatus = { kind: 'reconnecting', attempt: e.attempt, delay: e.delay };
        this.el.headerStatus.textContent = this.t.statusReconnecting(e.attempt, e.delay);
        this.applyConnStatusStyle(true);
        break;
      case 'ack':
        if (e.payload.state === 'queued') {
          this.updateMessageState(e.clientMsgId, 'sent');
        } else if (e.payload.state === 'failed') {
          this.updateMessageState(e.clientMsgId, 'failed');
          this.addMessage({
            id: 'sys-' + Date.now(),
            side: 'system',
            text: this.t.ackFailed(e.payload.reason ?? e.payload.code ?? 'unknown'),
            ts: Date.now(),
          });
        }
        break;
      case 'message': {
        // self-echo（发送方自己的回环帧）：把 self 消息的 v.id 从 clientMsgId
        // (UUIDv4) 回填成 server msgId(UUIDv7)。不回填会引两个连锁 bug:
        //   1) applyPeerRead 用字典序 v.id <= lastReadMsgId 命中失败,坐席已读
        //      永远在第一条后失效(UUIDv4 vs UUIDv7 字典序随机);
        //   2) onSendClick 每次都 ensureReady → fetchHistory → loadHistory,
        //      此时 Map 里的 self 消息 key 还是 clientMsgId,loadHistory 拿到
        //      的是 server msgId,this.msgs.has(view.id) 永远 false,DOM 重复
        //      渲染上一条气泡。同 admin-console e5dcb34 修法。
        if (e.payload.echo) {
          const cid = e.payload.clientMsgId;
          const sid = e.payload.msgId;
          if (cid && sid && cid !== sid) {
            const v = this.msgs.get(cid);
            if (v) {
              v.id = sid;
              this.msgs.delete(cid);
              this.msgs.set(sid, v);
              const node = this.el.messages.querySelector<HTMLElement>(
                `[data-msg-id="${CSS.escape(cid)}"]`,
              );
              if (node) node.setAttribute('data-msg-id', sid);
            }
          }
          break;
        }
        // 对端发了消息，无论是 bot 还是坐席，访客已脱队（仍可保留 bot 消息后等待坐席的场景，简化处理：直接隐藏）
        this.hideQueueBar();
        // 对端消息：text 走文本气泡，image/video/file 走附件气泡。
        const ct = e.payload.contentType;
        const senderName = this.resolveSenderName(e.payload.senderKey);
        const quoteView = this.quoteFromPayload(e.payload.quote);
        if (ct === 'image' || ct === 'video' || ct === 'file') {
          const meta = e.payload.content as unknown as AttachmentMeta;
          this.addMessage({
            id: e.payload.msgId,
            side: 'peer',
            senderName,
            attachment: { kind: ct, meta },
            ts: e.payload.createdAt || Date.now(),
            quote: quoteView,
          });
        } else {
          const text = String(e.payload.content ?? '');
          this.addMessage({
            id: e.payload.msgId,
            side: 'peer',
            senderName,
            text,
            ts: e.payload.createdAt || Date.now(),
            quote: quoteView,
          });
        }
        // 记录对端最新消息 id；如果浮窗已打开，立即回上 msg.read
        this.latestPeerMsgId = e.payload.msgId;
        if (this.open) {
          this.maybeSendRead();
        }
        if (!this.open) {
          this.unread += 1;
          this.el.launcher.setAttribute('data-unread', String(this.unread));
          if (shouldNotify(this.open)) {
            if (this.opts.soundEnabled !== false) playBeep();
            if (this.opts.notificationEnabled !== false) {
              const preview =
                ct === 'image' ? this.t.mediaImage :
                ct === 'video' ? this.t.mediaVideo :
                ct === 'file' ? this.t.mediaFile :
                String(e.payload.content ?? '').slice(0, 80);
              void showNotification(
                this.resolveTitle(),
                preview,
                'leke-' + (this.opts.getConvId() || 'msg'),
                () => this.toggle(true),
              );
            }
          }
        }
        break;
      }
      case 'notice':
        if (e.payload.code === 'read_receipt_changed') {
          // 租户实时切换已读回执开关:按新值重渲全部已读显示,无需刷新。
          this.setReadReceiptEnabled((e.payload as { readReceiptEnabled?: boolean }).readReceiptEnabled !== false);
        } else if (e.payload.code === 'conv_idle_timeout') {
          // 与 conv_ended 对齐的软结束：不禁用输入，访客继续打字时 autoRestartIfEnded
          // → restartConversation 清 convId → ensureReady 新建会话连 WS。
          // 同步调 onAgentEnded 清 stored convId，避免刷新后命中已 ended 的 conv 走复用分支。
          if (!this.msgs.has('sys-ended-by-idle')) {
            this.addMessage({
              id: 'sys-ended-by-idle',
              side: 'system',
              text: this.t.idleTimeoutNotice,
              ts: Date.now(),
            });
          }
          this.ended = true;
          this.opts.onAgentEnded?.();
        } else if (e.payload.code === 'conv_idle_warning') {
          const disc = (e.payload as { discMinutes?: number }).discMinutes ?? 3;
          this.addMessage({
            id: 'sys-idle-warn-' + Date.now(),
            side: 'system',
            text: this.t.idleWarningNotice(disc),
            ts: Date.now(),
          });
        } else if (e.payload.code === 'queue_position') {
          const pos = (e.payload as { position?: number }).position ?? 0;
          const total = (e.payload as { total?: number }).total ?? 0;
          this.showQueueBar(pos, total);
        } else if (e.payload.code === 'queue_timeout') {
          // 排队累计超 90 分钟仍无人接入：服务端强制断开
          this.hideQueueBar();
          this.addMessage({
            id: 'sys-queue-timeout-' + Date.now(),
            side: 'system',
            text: this.t.queueTimeoutNotice,
            ts: Date.now(),
          });
          this.markEnded();
        } else if (e.payload.code === 'conv_transferred') {
          const name =
            (e.payload as { targetAgentName?: string }).targetAgentName || this.t.senderAgent;
          // 更新当前会话坐席名：后续消息（含历史）都显示为新坐席
          this.agentName = name;
          this.syncWelcomeSenderName();
          this.addMessage({
            id: 'sys-transfer-' + Date.now(),
            side: 'system',
            text: this.t.transferredNotice(name),
            ts: Date.now(),
          });
        } else if (e.payload.code === 'conv_ended' && !this.endedByMe) {
          // 客服主动结束：不锁死浮窗（不渲染评分/重启按钮、不禁用输入框），
          // 只给一行轻量提示，并把 this.ended 标记为 true。访客继续打字时，
          // onSendClick → autoRestartIfEnded → resetEndedUI 会自动移除该提示并重连。
          // 固定 id：幂等避免重复展示；resetEndedUI 据此清理。
          //
          // endedByMe 守卫：访客自己点「结束对话」时后端同样会给访客回这一帧
          // （双方同一种帧，不带发起方），markEnded() 已经给过中性的「会话已
          // 结束。」，再插一条「坐席已结束对话」就归因错了。此路径的 stored
          // convId 由宿主的 endConversation 回调自己清，不缺 onAgentEnded。
          if (!this.msgs.has('sys-ended-by-agent')) {
            this.addMessage({
              id: 'sys-ended-by-agent',
              side: 'system',
              text: this.t.endedByAgentNotice,
              ts: Date.now(),
            });
          }
          this.ended = true;
          // 通知宿主清 stored convId：避免刷新后 hadStored 触发自动恢复 → GetOrCreate 进队列
          this.opts.onAgentEnded?.();
        }
        break;
      case 'read':
        this.applyPeerRead(e.lastReadMsgId);
        break;
      case 'recall':
        this.removeRecalledMessage(e.payload.msgId);
        break;
      case 'queue-dead':
        this.hideQueueBar();
        this.addMessage({
          id: 'sys-queue-dead',
          side: 'system',
          text: this.t.queueDisconnectedNotice,
          ts: Date.now(),
        });
        this.markEnded();
        break;
      case 'error':
        // 底层 ws.onerror 在掉线/握手失败时会反复触发,且 SDK 自己会重连,不给用户看
        if (e.message === 'websocket error') {
          console.warn('[leke-chat] websocket error');
          break;
        }
        this.addMessage({
          id: 'err-' + Date.now(),
          side: 'system',
          text: this.t.genericError(e.message),
          ts: Date.now(),
        });
        break;
    }
  }

  private addMessage(v: MessageView) {
    // 重连补拉（loadHistory/syncReconnectHistory）已按 msgId 幂等去重，但实时 msg.recv
    // 帧不走 loadHistory，直接调 addMessage——如果同一条消息已由补拉渲染进 DOM，这里不加
    // 检查会第二条 DOM 重复渲染（访客看到同一条消息出现两次）。
    if (this.msgs.has(v.id)) return;
    this.appendDaySeparator(v.ts);
    this.msgs.set(v.id, v);
    const node = this.renderMessage(v);
    this.el.messages.appendChild(node);
    this.trimMessages();
    this.el.messages.scrollTop = this.el.messages.scrollHeight;
  }

  // appendSysLine：追加一条居中灰条系统行（与"会话已结束"同款），不入 msgs/不参与去重。
  private appendSysLine(text: string): void {
    const wrap = document.createElement('div');
    wrap.className = 'leke-msg-wrap leke-msg-wrap-sys';
    const sys = document.createElement('div');
    sys.className = 'leke-msg leke-msg-system';
    sys.textContent = text;
    wrap.appendChild(sys);
    this.el.messages.appendChild(wrap);
  }

  private dayKey(ts: number): string {
    const d = new Date(ts);
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  }

  // 跨自然日时插入一条日期分隔（历史与实时消息共用，按本地时区分天）。
  private appendDaySeparator(ts: number): void {
    const k = this.dayKey(ts);
    if (k === this.lastDayKey) return;
    this.lastDayKey = k;
    this.appendSysLine(this.t.dateLabel(new Date(ts)));
  }

  /** 回收顶部最旧 DOM 节点 + 对应 msgs 记录，使两者一起有界。 */
  private trimMessages() {
    const box = this.el.messages;
    while (box.children.length > MAX_RENDERED_MESSAGES) {
      let first = box.firstElementChild as HTMLElement | null;
      // 历史折叠区固定在顶部、不计入裁剪：跳过它，回收其后的最旧消息
      if (first && first === this.histZoneEl) {
        first = first.nextElementSibling as HTMLElement | null;
      }
      if (!first) break;
      const id = first.dataset.msgId;
      if (id) this.msgs.delete(id);
      box.removeChild(first);
    }
  }

  /** 从 REST 历史响应的 quote 字段转成 view */
  /** 引用块里的发送者名：坐席一律显示当前会话坐席名，与气泡头部保持一致。
   *  服务端给的是坐席后台的真名(display_name)，访客侧不该看到——客户可能配了对外昵称。 */
  private quoteSenderName(senderType: MessageQuoteView['senderType'], raw?: string): string | undefined {
    if (senderType === 'agent') return this.agentName || this.t.senderAgent;
    return raw;
  }

  private quoteFromHistory(q: HistoryQuote | undefined): MessageQuoteView | undefined {
    if (!q) return undefined;
    return {
      msgId: q.msgId,
      senderType: q.senderType,
      senderName: this.quoteSenderName(q.senderType, q.senderName),
      contentType: q.contentType,
      summary: this.quoteSummaryFor(q.contentType, q.content, q.recalled),
      recalled: q.recalled,
    };
  }

  /** 把服务端下行的 quote payload 转成 widget 内部 view 结构。 */
  private quoteFromPayload(q: import('./protocol').QuotePreviewFrame | undefined): MessageQuoteView | undefined {
    if (!q) return undefined;
    return {
      msgId: q.msgId,
      senderType: q.senderType,
      senderName: this.quoteSenderName(q.senderType, q.senderName),
      contentType: q.contentType,
      summary: this.quoteSummaryFor(q.contentType, q.content, q.recalled),
      recalled: q.recalled,
    };
  }

  /** 给 contentType / content 生成纯文本预览，>80 截断 */
  private quoteSummaryFor(contentType: string, content: unknown, recalled?: boolean): string {
    if (recalled) return this.t.msgRecalled;
    if (contentType === 'image') return this.t.mediaImage;
    if (contentType === 'video') return this.t.mediaVideo;
    if (contentType === 'file') return this.t.mediaFile;
    if (contentType !== 'text') return '[' + contentType + ']';
    let s = '';
    if (typeof content === 'string') s = content;
    else if (content != null) s = String(content);
    return s.length > 80 ? s.slice(0, 80) + '…' : s;
  }

  /** 用户点击气泡上的「引用」按钮 → 设当前 quoting + 渲染引用条 */
  private pickQuote(m: MessageView): void {
    if (m.recalled || m.local || m.side === 'system' || m.state === 'sending' || m.state === 'failed') return;
    this.quoting = {
      msgId: m.id,
      senderType: m.side === 'self' ? 'visitor' : (this.agentName ? 'agent' : 'bot'),
      senderName: m.side === 'self' ? undefined : m.senderName,
      contentType: m.attachment ? m.attachment.kind : 'text',
      summary: m.attachment ? '[' + m.attachment.kind + ']' : (m.text ?? '').slice(0, 80),
      recalled: false,
    };
    this.renderQuoteBar();
    this.el.textarea.focus();
  }

  private clearQuote(): void {
    this.quoting = null;
    this.renderQuoteBar();
  }

  /** 渲染或更新输入框上方的引用条；无引用时移除 */
  private renderQuoteBar(): void {
    if (!this.quoting) {
      if (this.quoteBarEl) {
        this.quoteBarEl.remove();
        this.quoteBarEl = null;
      }
      return;
    }
    if (!this.quoteBarEl) {
      this.quoteBarEl = document.createElement('div');
      this.quoteBarEl.className = 'leke-quote-bar';
      // 插在 .leke-input 容器的"前面"（外面），让它独占一行；
      // 不能插进 .leke-input 内部 —— 那是横向 flex，会把引用条挤在 textarea 旁边
      const inputEl = this.el.textarea.parentElement;
      inputEl?.parentElement?.insertBefore(this.quoteBarEl, inputEl);
    }
    this.quoteBarEl.replaceChildren();
    const txt = document.createElement('div');
    txt.className = 'leke-quote-bar-text';
    const arrow = document.createElement('span');
    arrow.className = 'leke-quote-arrow';
    arrow.textContent = '↩';
    txt.appendChild(arrow);
    txt.appendChild(document.createTextNode(this.t.quoteBarLabel));
    const from = this.quoting.senderName || (
      this.quoting.senderType === 'agent' ? this.t.senderAgent :
      this.quoting.senderType === 'bot' ? this.t.senderBot :
      this.t.senderSelf
    );
    const strong = document.createElement('strong');
    strong.textContent = from;
    txt.appendChild(strong);
    txt.appendChild(document.createTextNode('：'));
    const sum = document.createElement('span');
    sum.className = 'leke-quote-bar-summary';
    sum.textContent = this.quoting.summary;
    txt.appendChild(sum);
    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'leke-quote-bar-clear';
    clear.textContent = '×';
    clear.setAttribute('aria-label', this.t.quoteBarClearAriaLabel);
    clear.addEventListener('click', () => this.clearQuote());
    this.quoteBarEl.appendChild(txt);
    this.quoteBarEl.appendChild(clear);
  }

  private renderMessage(v: MessageView, readonly = false): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'leke-msg-wrap ' + (v.side === 'self' ? 'leke-msg-wrap-self' : v.side === 'peer' ? 'leke-msg-wrap-peer' : 'leke-msg-wrap-sys');
    wrap.dataset.msgId = v.id;

    if (v.side === 'system') {
      const sys = document.createElement('div');
      sys.className = 'leke-msg leke-msg-system';
      sys.textContent = v.text ?? '';
      wrap.appendChild(sys);
      return wrap;
    }

    // peer 消息：气泡左侧留一格品牌 logo 头像。始终建节点、无 logo 时隐藏，
    // 这样租户后来配上/清掉 logo 时 setLogo 只需切 src，不必重渲已有消息。
    let box: HTMLElement = wrap;
    if (v.side === 'peer') {
      wrap.classList.add('leke-msg-wrap-avatar');
      const avatar = document.createElement('img');
      avatar.className = 'leke-msg-avatar';
      avatar.alt = '';
      if (this.logoUrl) avatar.src = this.logoUrl;
      else avatar.style.display = 'none';
      wrap.appendChild(avatar);
      const col = document.createElement('div');
      col.className = 'leke-msg-col';
      wrap.appendChild(col);
      box = col;
    }

    // peer 消息上方显示发送者名 + 时间（同行）
    if (v.side === 'peer') {
      const head = document.createElement('div');
      head.className = 'leke-msg-head';
      if (v.senderName) {
        const nameEl = document.createElement('span');
        nameEl.className = 'leke-msg-sender';
        nameEl.textContent = v.senderName;
        head.appendChild(nameEl);
      }
      const tEl = document.createElement('span');
      tEl.className = 'leke-msg-time';
      tEl.textContent = formatHHMM(v.ts);
      head.appendChild(tEl);
      box.appendChild(head);
    }

    const div = document.createElement('div');
    div.className = 'leke-msg ' + (v.side === 'self' ? 'leke-msg-self' : 'leke-msg-peer');

    // 引用块：若该消息引用了历史消息，渲染在内容之前
    if (v.quote && !v.recalled) {
      const q = v.quote;
      const qb = document.createElement('div');
      qb.className = 'leke-quote-block';
      const head = document.createElement('div');
      head.className = 'leke-quote-head';
      const arrow = document.createElement('span');
      arrow.className = 'leke-quote-arrow';
      arrow.textContent = '↩';
      head.appendChild(arrow);
      const from = document.createElement('span');
      from.className = 'leke-quote-from';
      from.textContent = q.senderName || (
        q.senderType === 'agent' ? this.t.senderAgent :
        q.senderType === 'bot' ? this.t.senderBot :
        q.senderType === 'system' ? this.t.senderSystem :
        this.t.senderSelf
      );
      head.appendChild(from);
      qb.appendChild(head);
      const qbody = document.createElement('div');
      qbody.className = 'leke-quote-body';
      qbody.textContent = q.summary;
      qb.appendChild(qbody);
      qb.addEventListener('click', () => {
        const t = this.el.messages.querySelector('[data-msg-id="' + cssEscape(q.msgId) + '"]');
        if (t) {
          (t as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          t.classList.add('leke-quote-flash');
          window.setTimeout(() => t.classList.remove('leke-quote-flash'), 1500);
        }
      });
      div.appendChild(qb);
    }

    const body = document.createElement('div');
    if (v.attachment) {
      const a = v.attachment;
      if (a.kind === 'image') {
        // 一条消息可携带多张图片(images 数组);回退到单张 url
        const imgs =
          Array.isArray(a.meta.images) && a.meta.images.length
            ? a.meta.images
            : [{ url: a.meta.url, name: a.meta.name }];
        const grid = document.createElement('div');
        grid.className = 'leke-attachment-images' + (imgs.length > 1 ? ' leke-multi' : '');
        for (const it of imgs) {
          const img = document.createElement('img');
          img.src = it.url;
          img.alt = it.name || a.meta.name;
          img.className = 'leke-attachment-image';
          grid.appendChild(img);
        }
        body.appendChild(grid);
      } else if (a.kind === 'video') {
        const video = document.createElement('video');
        video.src = a.meta.url;
        video.controls = true;
        video.preload = 'metadata';
        video.className = 'leke-attachment-video';
        body.appendChild(video);
      } else {
        const link = document.createElement('a');
        link.href = a.meta.url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.className = 'leke-attachment-file';
        link.textContent = '📄 ' + a.meta.name;
        body.appendChild(link);
      }
      // 图文合并：附件自带图注时，在媒体下方同气泡显示
      const cap = a.meta.text;
      if (cap) {
        const capEl = document.createElement('div');
        capEl.className = 'leke-attachment-caption';
        capEl.textContent = cap;
        body.appendChild(capEl);
      }
    } else {
      body.textContent = v.text ?? '';
    }
    div.appendChild(body);
    box.appendChild(div);

    // self 消息底部展示时间 + 发送状态；peer 的时间已经在 head 里
    if (v.side === 'self') {
      const foot = document.createElement('div');
      foot.className = 'leke-msg-foot leke-msg-foot-self';
      const ts = document.createElement('span');
      ts.className = 'leke-msg-time';
      ts.textContent = formatHHMM(v.ts);
      foot.appendChild(ts);
      const stateEl = document.createElement('span');
      stateEl.className = 'leke-msg-meta';
      stateEl.textContent = this.stateLabel(v.state);
      foot.appendChild(stateEl);
      wrap.appendChild(foot);
    }

    // 引用按钮：hover/tap 气泡时浮出在右上角（system 消息已经在前面 return 了）。
    // readonly（跨会话历史对话）不可引用，与坐席端历史只读一致；
    // local（欢迎语等本地消息）服务端无对应记录，引用会指向不存在的 msgId，同样不给按钮。
    if (!readonly && !v.local && !v.recalled && v.state !== 'sending' && v.state !== 'failed') {
      const quoteBtn = document.createElement('button');
      quoteBtn.type = 'button';
      quoteBtn.className = 'leke-quote-btn';
      quoteBtn.title = this.t.quoteButtonTitle;
      quoteBtn.textContent = this.t.quoteButtonLabel;
      quoteBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        this.pickQuote(v);
      });
      wrap.appendChild(quoteBtn);
    }

    return wrap;
  }

  private stateLabel(state: MessageView['state']): string {
    if (state === 'sending') return this.t.msgStateSending;
    if (state === 'failed') return this.t.msgStateFailed;
    if (state === 'read') return this.t.msgStateRead;
    return this.t.msgStateSent;
  }

  // 服务端广播 msg.recall：撤回的消息从访客视图中直接删除（不留占位）。
  // 消息不在当前视图（未加载/已 trim）时忽略；历史重载时 loadHistory 也会跳过。
  private removeRecalledMessage(id: string) {
    if (!this.msgs.has(id)) return;
    this.msgs.delete(id);
    const node = this.el.messages.querySelector<HTMLDivElement>(
      `[data-msg-id="${cssEscape(id)}"]`,
    );
    if (node) node.remove();
  }

  private updateMessageState(id: string, state: MessageView['state']) {
    const v = this.msgs.get(id);
    if (!v) return;
    v.state = state;
    const node = this.el.messages.querySelector<HTMLDivElement>(
      `[data-msg-id="${CSS.escape(id)}"] .leke-msg-meta`,
    );
    if (node) {
      node.textContent = this.stateLabel(state);
    }
  }
}

// formatHHMM 把毫秒时间戳格式化为 HH:MM。
function formatHHMM(ts: number): string {
  const d = new Date(ts);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

// cssEscape 转义 querySelector 用的 attr 值（UUID 一般安全，但保险起见仍处理）。
function cssEscape(s: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(s);
  return s.replace(/["\\]/g, '\\$&');
}
